/**
 * Browser-side request key for one invoice send (V220 send reservation).
 *
 * The key lets a retry after a lost response replay the original outcome
 * instead of emailing twice. It must not outlive that purpose: a key left in
 * sessionStorage for days would make a same-content reminder look like the
 * old send. So it is cleared as soon as the outcome is known, and ignored once
 * it is older than SEND_INTENT_WINDOW_MS (the backend's replay window, 15 min).
 *
 * Only the opaque key and its creation time are stored; never addresses or notes.
 */

export const SEND_INTENT_WINDOW_MS = 15 * 60 * 1000;

const storageKey = (invoiceId: string) => `invoice-send-intent:${invoiceId}`;

interface StoredIntent {
  key: string;
  createdAt: number;
}

function safeStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** The live key for this invoice, or null when none is stored or it has expired. */
export function readSendIntent(invoiceId: string, now: number = Date.now()): string | null {
  const storage = safeStorage();
  const raw = storage?.getItem(storageKey(invoiceId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredIntent>;
    if (typeof parsed.key === 'string' && typeof parsed.createdAt === 'number'
        && now - parsed.createdAt >= 0 && now - parsed.createdAt < SEND_INTENT_WINDOW_MS) {
      return parsed.key;
    }
  } catch {
    // An unreadable or old-format value has no timestamp: treat as expired.
  }
  storage?.removeItem(storageKey(invoiceId));
  return null;
}

export function storeSendIntent(invoiceId: string, key: string, now: number = Date.now()): void {
  const value: StoredIntent = { key, createdAt: now };
  safeStorage()?.setItem(storageKey(invoiceId), JSON.stringify(value));
}

export function clearSendIntent(invoiceId: string): void {
  safeStorage()?.removeItem(storageKey(invoiceId));
}

/** The structured error body this backend wraps under `error` (or `detail`). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sendErrorDetail(error: any): any {
  return error?.data?.error || error?.data?.detail || error?.response?.data?.detail;
}

/**
 * True when a failed send request has a KNOWN outcome, so the key can go.
 * Unknown means the email may have gone out: a network failure with no
 * response, a send still being checked, or an unexplained server error.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isKnownSendOutcome(error: any): boolean {
  const detail = sendErrorDetail(error);
  if (detail && typeof detail === 'object') {
    if (detail.state === 'FAILED' || detail.state === 'SENT') return true;
    if (detail.code === 'invoice_status_blocks_send') return true;
    if (detail.code === 'invoice_send_in_progress' || detail.code === 'invoice_send_delivery_unknown') return false;
  }
  const status = error?.status;
  return status === 400 || status === 403 || status === 404 || status === 413 || status === 422;
}

/** "Sep 16, 2026 at 11:04 AM" in the viewer's own time zone. */
export function formatSentAt(iso: string | null | undefined): string {
  if (!iso) return 'an earlier time';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'an earlier time';
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}
